import uniqBy from 'lodash/uniqBy';
import { toSlug } from '@framework/url/helpers';
import { buildQueryParams } from '@/creditflowresearch/application/helpers/buildQueryParams';
import type { IDealLead, IDealLeadsGroup, IDealLeadsQuery } from './types';

export interface IFilledLeadsGroup {
  role: string,
  leads: IDealLead[],
}

const DEFAULT_INLINE_LIMIT = 4;
const FALLBACK_QUERY_KEYS: (keyof IDealLeadsQuery)[] = ['products', 'regions'];

const getSortKey = (lead: IDealLead): string => (lead.abbreviation || lead.name).toLowerCase();

export const getLeadKey = (lead: IDealLead): string => lead.id ?? String(lead.viewId ?? lead.name);

/**
 * Drops roles without banks and orders every role's banks by abbreviation, without touching the input.
 */
export const getFilledGroups = (groups: IDealLeadsGroup[]): IFilledLeadsGroup[] => groups
  .map(({ role, leads }) => ({
    role,
    leads: [...(leads ?? [])].sort((a, b) => getSortKey(a).localeCompare(getSortKey(b))),
  }))
  .filter(({ leads }) => leads.length > 0);

/**
 * The banks shown in the cell: roles in the given order, a bank with several roles once, at most `limit` of them.
 */
export const splitInlineLeads = (
  groups: IFilledLeadsGroup[],
  limit: number = DEFAULT_INLINE_LIMIT,
): { visibleLeads: IDealLead[], hasHiddenLeads: boolean } => {
  const leads = uniqBy(groups.flatMap((group) => group.leads), getLeadKey);

  return {
    visibleLeads: leads.slice(0, limit),
    hasHiddenLeads: leads.length > limit,
  };
};

const COUNTABLE_ROLE = /(Lead|Manager|Coordinator)$/;

/**
 * "Joint Lead" for one bank, "Joint Leads" for several.
 * "Billing & Delivering" names a duty, not a bank, and stays as is.
 */
export const getRoleLabel = (role: string, leadsCount: number): string => (
  leadsCount > 1 && COUNTABLE_ROLE.test(role) ? `${ role }s` : role
);

export const getLeadPathname = (lead: IDealLead): string | null => {
  if (lead.url) {
    return /^(https?:)?\/\//.test(lead.url) || lead.url.startsWith('/') ? lead.url : `/${ lead.url }`;
  }

  if (lead.viewId) {
    return `/underwriter-${ lead.viewId }-${ toSlug(lead.name) }`;
  }

  return null;
};

/**
 * Lead links keep the page's filters. A product or region the page has no filter for comes from the deal.
 */
export const getLeadsQuery = (
  searchParams: Pick<URLSearchParams, 'entries'> | null,
  fallbackQuery: IDealLeadsQuery = {},
): string => {
  const query = new URLSearchParams(buildQueryParams(Object.fromEntries(searchParams?.entries() ?? [])));

  FALLBACK_QUERY_KEYS.forEach((key) => {
    if (!query.has(key) && fallbackQuery[key]) {
      query.set(key, fallbackQuery[key]);
    }
  });

  return query.toString();
};
